import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, NavLink, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { BackdropEditor } from "@/components/banner/BackdropPrintDialog";
import { deDate } from "@/lib/bannerLayout";
import { Button } from "@/components/ui/button";
import { Frame, Loader2 } from "lucide-react";

const current = (b) => b.bookings.filter((x) => !x.expired);

function Assign({ backdrop, onDone }) {
  const [partners, setPartners] = useState([]);
  const [cid, setCid] = useState("");
  useEffect(() => { api.get("/customers", { params: { type: "partner" } }).then((r) => setPartners((r.data.items || r.data).filter((p) => !p.archived))).catch(() => {}); }, []);
  const go = () => api.post(`/backdrop-print/${backdrop.id}/assign`, { customer_id: cid }).then(() => { toast.success("Kunde zugeordnet – Fläche im Profil als gebucht eingetragen"); onDone(cid); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white p-4" data-testid="backdrop-assign">
      <span className="text-sm text-slate-600">Kunde zuordnen:</span>
      <select value={cid} onChange={(e) => setCid(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="backdrop-assign-select">
        <option value="">Partner / Sponsor wählen</option>
        {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <Button size="sm" disabled={!cid} onClick={go} className="bg-[#005448] hover:bg-[#00433a]" data-testid="backdrop-assign-save">Zuordnen</Button>
    </div>
  );
}

function Editor({ b, reload }) {
  const list = current(b);
  const [cid, setCid] = useState(list[0]?.id || "");
  useEffect(() => { setCid(current(b)[0]?.id || ""); }, [b.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4" data-testid="backdrop-designer-editor">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{b.type_name}</p>
        <h2 className="text-2xl font-semibold text-slate-900">{b.name}{b.location && <span className="text-slate-400 font-normal"> · {b.location}</span>}</h2>
      </div>
      {list.length > 1 && <div className="flex flex-wrap gap-2">{list.map((x) => (
        <button key={x.id} type="button" onClick={() => setCid(x.id)} className={`rounded-full border px-3 py-1 text-sm ${cid === x.id ? "border-[#005448] bg-[#005448] text-white" : "border-slate-200 bg-white"}`} data-testid={`backdrop-partner-${x.id}`}>{x.name}{x.from ? ` ab ${deDate(x.from)}` : ""}</button>
      ))}</div>}
      {list.length === 1 && <p className="text-sm text-slate-600">Kunde: <Link to={`/partner/${list[0].id}`} className="font-medium text-slate-900 hover:underline">{list[0].name}</Link>{list[0].until ? ` · bis ${deDate(list[0].until)}` : ""}</p>}
      {!list.length && <Assign backdrop={b} onDone={(id) => { reload(); setCid(id); }} />}
      {cid && <BackdropEditor key={cid} customerId={cid} spaceId={b.id} />}
    </div>
  );
}

export default function BackdropDesigner() {
  const { spaceId } = useParams();
  const [list, setList] = useState(null);
  const load = useCallback(() => { api.get("/backdrop-print/list").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  useEffect(() => { load(); }, [load]);
  if (!list) return <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!spaceId && list.length) return <Navigate to={`/partner/backdrops/${list[0].id}`} replace />;
  const b = list.find((x) => x.id === spaceId);
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="backdrop-designer">
      <div>
        <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3"><Frame className="text-[#005448]" /> Backdrops</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Backdrop wählen, Kunde zuordnen, Logo hineinziehen und platzieren. Danach Freigabe-PDF für den Kunden und Druck-PDF für den Hersteller.</p>
      </div>
      {!list.length ? <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-600" data-testid="backdrop-empty">Im <Link to="/partner/leistungskatalog" className="text-blue-600 hover:underline">Leistungskatalog</Link> bei der Leistungsart „Backdrops/Tennisblenden“ den Schalter „Druckdatei (Logo schwarz)“ einschalten.</div>
        : <div className="grid gap-6 grid-cols-[minmax(0,1fr)] lg:grid-cols-[240px_minmax(0,1fr)]">
          <nav className="space-y-1.5" data-testid="backdrop-list">{list.map((x) => (
            <NavLink key={x.id} to={`/partner/backdrops/${x.id}`} data-testid={`backdrop-list-item-${x.id}`} className={({ isActive }) => `block rounded-lg border px-3 py-2.5 transition-colors ${isActive ? "border-[#005448] bg-[#005448] text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className="block text-sm font-semibold truncate">{x.name}{x.location ? ` · ${x.location}` : ""}</span>
              <span className="block text-xs opacity-75 truncate">{current(x).map((c) => c.name).join(", ") || "frei"}</span>
            </NavLink>
          ))}</nav>
          {b && <Editor b={b} reload={load} />}
        </div>}
    </div>
  );
}
